import { DotLegalMultiSelect, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { Box, Grid, GridSize, TableCell, TableRow, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import React, { useState } from "react";
import TableWithControlsWrapper from "../../common/components/tableWithControlsWrapper/TableWithControlsWrapper";
import DotLegalModal from "../../common/modal/DotLegalModal";
import DotLegalTableWithControls, { ITableHeader } from "../../common/tableWithControls/DotLegalTableWithControls";
import AddEditUserDialog from "../../core/users/AddEditUserDialog";
import ResendInvitation from "../../core/users/ResendInvitation";
import invitationIcon from "../icons/Invitation.svg";
import { ApplicationUser, ConfigurableApplicationUser, UserRole, UsersTableModel } from "./User.types";
import UserOverflowMenu from "./UserOverflowMenu";
import { deleteUser, getCountryOptions, getMenuOptions, initialConfigurableUser, mapApplicationUserToConfigurableUser } from "./UserService";
import { useUsersTable } from "./UsersTable.hooks";
import useUserTableStyles from "./UserTable.styles";

type UserTableProps = {};

function UserTable(props: UserTableProps) {
    const classes = useUserTableStyles();
    const snackbar = useDotLegalSnackbar();
    const {
        isLoading,
        users,
        organisationFilterOptions,
        userRoleFilterOptions,
        selectedOrganisations,
        selectedUserRoles,
        setSelectedOrganisations,
        setSelectedUserRoles,
        refetchUsers,
    } = useUsersTable();

    const [configurableUser, setConfigurableUser] = useState<ConfigurableApplicationUser>({
        ...initialConfigurableUser,
    });
    const [configureUserModalIsOpen, setConfigureUserModalIsOpen] = useState<boolean>(false);
    const [resendInvitationModalIsOpen, setResendInvitationModalIsOpen] = useState<boolean>(false);

    const [deleteUseModelOpen, setDeleteUserModelOpen] = useState<boolean>(false);
    const [waitingForServerDelete, setWaitingForServerDelete] = useState(false);

    const onInvitationModalClosed = (): void => setResendInvitationModalIsOpen(false);
    const configureUser = (user: ConfigurableApplicationUser = { ...initialConfigurableUser }): void => {
        setConfigurableUser(user);
        setConfigureUserModalIsOpen(true);
    };

    const resendInvitation = (user: ApplicationUser): void => {
        setConfigurableUser(mapApplicationUserToConfigurableUser(user));
        setResendInvitationModalIsOpen(true);
    };

    const handleUserSaved = (): void => {
        snackbar.show("User saved successfully", "success");
        setConfigureUserModalIsOpen(false);
        refetchUsers();
    };

    const configureDeleteUser = (user: ConfigurableApplicationUser = { ...initialConfigurableUser }): void => {
        setConfigurableUser(user);
        setDeleteUserModelOpen(true);
    };
    const deleteConfiguredUser = (): void => {
        setWaitingForServerDelete(true);
        deleteUser(configurableUser.id!)
            .then(() => {
                snackbar.show("User deleted successfully", "success");
                setWaitingForServerDelete(false);
                setDeleteUserModelOpen(false);
                refetchUsers();
            })
            .catch((reason) => {
                snackbar.show(reason.message, "error");
                setWaitingForServerDelete(false);
            });
    };

    const getSearchFields = () => {
        const tableGridProps: { item: boolean; xs: GridSize; sm: GridSize; md: GridSize; lg: GridSize } = {
            item: true,
            xs: 12,
            sm: 6,
            md: 2,
            lg: 2,
        };

        return (
            <React.Fragment>
                <Grid {...tableGridProps}>
                    <DotLegalMultiSelect
                        onChange={setSelectedOrganisations}
                        chosenOptions={selectedOrganisations}
                        label={"Organisation(s)"}
                        placeholder={"Organisation(s)"}
                        isLoading={isLoading}
                        options={organisationFilterOptions}
                        noMargin
                        Limit={1}
                        noOptionsLabel={"No options"}
                    />
                </Grid>
                <Grid {...tableGridProps}>
                    <DotLegalMultiSelect
                        onChange={setSelectedUserRoles}
                        chosenOptions={selectedUserRoles}
                        label={"User role(s)"}
                        placeholder={"User role(s)"}
                        isLoading={isLoading}
                        options={userRoleFilterOptions}
                        noMargin
                        Limit={1}
                        noOptionsLabel={"No options"}
                    />
                </Grid>
            </React.Fragment>
        );
    };

    return (
        <>
            <TableWithControlsWrapper>
                <DotLegalTableWithControls
                    extraControls={getSearchFields()}
                    headers={getHeaders()}
                    getUserSpecificPageLength={() => 15}
                    labelRowsPerPage={"Test"}
                    labelSearch={"Search"}
                    noOptionsLabel={"No options"}
                    defaultOrderBy={"name"}
                    defaultOrder={"asc"}
                    isLoading={isLoading}
                    paginationLabelOf={"of"}
                    hideRowsPerPage
                    emptyText={"NO RESULTS FOUND. TRY ADJUSTING YOUR SEARCH CRITERIA."}
                    data={mapUsersToTableModel(users)}
                    renderRow={(r) => (
                        <TableRow
                            key={r.id}
                            onClick={(event) => {
                                configureUser(mapApplicationUserToConfigurableUser(r));
                            }}
                        >
                            <TableCell style={{ paddingLeft: 6 }}>{r.name}</TableCell>
                            <TableCell>{r.email}</TableCell>
                            <TableCell>
                                {r.countryCode} {r.phoneNumber}
                            </TableCell>
                            <TableCell>{r.userRoles.map((role) => role.name).join(", ")}</TableCell>
                            <TableCell>
                                <div className={classes.ellipsisCell}>{r.organizations.map((org) => org.name).join(", ")}</div>
                            </TableCell>
                            <TableCell>
                                <div className={classes.ellipsisCell}>{formatCountryAccess(r.countryAccess)}</div>
                            </TableCell>
                            <TableCell>
                                <div className={classes.ellipsisCell}>{formatMenuAccess(r.menuAccess, r.userRoles)}</div>
                            </TableCell>
                            <TableCell width={160} align="center">
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        resendInvitation(r);
                                    }}
                                    onKeyPress={() => {
                                        resendInvitation(r);
                                    }}
                                >
                                    <img src={invitationIcon} alt="" />
                                </div>
                            </TableCell>
                            <TableCell align="right">
                                <UserOverflowMenu
                                    user={configurableUser}
                                    openEditModal={() => configureUser(mapApplicationUserToConfigurableUser(r))}
                                    openDeleteModal={() => configureDeleteUser(mapApplicationUserToConfigurableUser(r))}
                                />
                            </TableCell>
                        </TableRow>
                    )}
                />
            </TableWithControlsWrapper>
            {configureUserModalIsOpen && (
                <AddEditUserDialog onClose={() => setConfigureUserModalIsOpen(false)} user={configurableUser} onSaved={handleUserSaved} />
            )}
            <DotLegalModal
                open={deleteUseModelOpen}
                onClose={() => setDeleteUserModelOpen(false)}
                title={`Delete User`}
                content={
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <React.Fragment>
                            <Typography sx={(theme) => ({ fontSize: theme.typography.pxToRem(14) })}>
                                {"Are you sure you want to delete "} <strong>{configurableUser.name}?</strong>
                            </Typography>
                        </React.Fragment>
                    </div>
                }
                action={
                    <React.Fragment>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                marginBottom: "40px",
                            }}
                        >
                            <Button
                                sx={{ marginRight: "30px", borderWidth: "medium", border: "1px solid" }}
                                color="secondary"
                                variant="outlined"
                                onClick={() => setDeleteUserModelOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button style={{ backgroundColor: "#F7447A" }} onClick={deleteConfiguredUser}>
                                Delete
                            </Button>
                        </Box>
                    </React.Fragment>
                }
            />
            <DotLegalModal
                content={<ResendInvitation invitee={configurableUser} onClose={onInvitationModalClosed} />}
                onClose={onInvitationModalClosed}
                open={resendInvitationModalIsOpen}
                title="Resend invitation"
            />
            <div style={{ paddingTop: 40, display: "flex", justifyContent: "flex-end" }}>
                <Button color="primary" onClick={() => configureUser()} variant="outlined">
                    Add user
                </Button>
            </div>
        </>
    );

    function getHeaders(): Array<ITableHeader<UsersTableModel>> {
        return [
            { property: "name", text: "Name", align: "left", showOnMobile: true, width: "300" },
            { property: "email", text: "Email", align: "left", showOnMobile: true, width: "300" },
            { property: "phoneNumber", text: "Phone", align: "left", showOnMobile: true, width: "300" },
            { property: "userRoles", text: "User Role(s)", align: "left", showOnMobile: true, width: "300" },
            { property: "organizations", text: "Organisation(s)", align: "left", showOnMobile: true, notSortable: true, width: "300" },
            { property: "countryAccess", text: "Country Access", align: "left", showOnMobile: false, notSortable: true, width: "200" },
            { property: "menuAccess", text: "Menu Access", align: "left", showOnMobile: false, notSortable: true, width: "200" },
            { property: "resendInvitation", text: "Resend Invitation(s)", align: "left", showOnMobile: true, notSortable: true, width: "150" },
        ];
    }

    function mapUsersToTableModel(users: Array<ApplicationUser>) {
        return users.map((u) => {
            return { ...u, resendInvitation: true };
        });
    }

    function formatCountryAccess(countryCodes: string[]): string {
        if (!countryCodes || countryCodes.length === 0) {
            return "All";
        }
        const allCountries = getCountryOptions();
        const countryNames = countryCodes.map((code) => {
            const country = allCountries.find((c) => c.code.toLowerCase() === code.toLowerCase());
            return country ? country.name : code.toUpperCase();
        });
        if (countryNames.length === 1) {
            return countryNames[0];
        }
        if (countryNames.length === 2) {
            return countryNames.join(", ");
        }
        return `${countryNames[0]}, ${countryNames[1]} +${countryNames.length - 2}`;
    }

    function formatMenuAccess(menuIds: number[], userRoles: UserRole[]): string {
        if (!menuIds || menuIds.length === 0) {
            // Check if user has "Read only" or "Client" role
            const roleNames = userRoles.map((role) => role.name);
            if (roleNames.includes("Read only")) {
                return "All (except Costs, Price Calculator)";
            }
            if (roleNames.includes("Client")) {
                return "All (except Price Calculator)";
            }
            return "All";
        }
        const allMenus = getMenuOptions();
        const menuNames = menuIds.map((id) => {
            const menu = allMenus.find((m) => m.id === id);
            return menu ? menu.name : `Menu ${id}`;
        });
        if (menuNames.length === 1) {
            return menuNames[0];
        }
        if (menuNames.length === 2) {
            return menuNames.join(", ");
        }
        return `${menuNames[0]}, ${menuNames[1]} +${menuNames.length - 2}`;
    }
}

export default UserTable;
